import React, { useState, useEffect } from 'react';
import VehicleSearch from './VehicleSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function Repairs({ token, user }) {
  const isMobile = useIsMobile();
  const [repairs, setRepairs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [savedMRReports, setSavedMRReports] = useState([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    description: '',
    reported_issue: '',
    current_mileage: '',
    // Categorised attachments
    quotes: [],
    damage_photos: [],
    mr_report_id: '',  // Selected saved M&R report
    mr_raw_file: null,  // Or upload raw M&R Excel
    other_files: []
  });
  const [submitModal, setSubmitModal] = useState({ 
    show: false, 
    repairId: null, 
    repair: null,
    estimatedCost: '', 
    extracting: false,
    extractedQuotes: [],
    historicalVendors: [],
    loadingHistory: false
  });
  const [analysisModal, setAnalysisModal] = useState({ show: false, repair: null, loading: false });
  const [editModal, setEditModal] = useState({ show: false, repair: null, newQuotes: [], newMRFile: null, newMRReportId: '', saving: false });
  const [deleteModal, setDeleteModal] = useState({ show: false, repair: null, deleting: false });
  const [actionDropdown, setActionDropdown] = useState(null); // Track which row's dropdown is open
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, mine

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionDropdown && !event.target.closest('.actions-dropdown-container')) {
        setActionDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [actionDropdown]);

  useEffect(() => {
    fetchRepairs();
    fetchVehicles();
    fetchSavedMRReports();
  }, []);

  const fetchRepairs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRepairs(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSavedMRReports = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/saved-reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedMRReports(data);
      }
    } catch (error) {
      console.error('Error fetching saved M&R reports:', error);
    }
  };

  // Filter saved reports by selected vehicle
  const filteredMRReports = savedMRReports.filter(
    r => !formData.vehicle_id || r.vehicle_no === formData.vehicle_id
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('vehicle_id', formData.vehicle_id);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('reported_issue', formData.reported_issue);
      if (formData.current_mileage) {
        formDataToSend.append('current_mileage', formData.current_mileage);
      }
      
      // Categorised attachments
      // Quotes
      if (formData.quotes && formData.quotes.length > 0) {
        for (let i = 0; i < formData.quotes.length; i++) {
          formDataToSend.append('quotes', formData.quotes[i]);
        }
      }
      
      // Damage photos
      if (formData.damage_photos && formData.damage_photos.length > 0) {
        for (let i = 0; i < formData.damage_photos.length; i++) {
          formDataToSend.append('damage_photos', formData.damage_photos[i]);
        }
      }
      
      // M&R Report - either saved report ID or raw file
      if (formData.mr_report_id) {
        formDataToSend.append('mr_report_id', formData.mr_report_id);
      } else if (formData.mr_raw_file) {
        formDataToSend.append('mr_raw_file', formData.mr_raw_file);
      }
      
      // Other files
      if (formData.other_files && formData.other_files.length > 0) {
        for (let i = 0; i < formData.other_files.length; i++) {
          formDataToSend.append('other_files', formData.other_files[i]);
        }
      }

      const response = await fetch(`${BACKEND_URL}/api/repairs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Repair request created! ID: ${data.id}` });
        fetchRepairs();
        setShowForm(false);
        setFormData({ 
          vehicle_id: '', 
          description: '', 
          reported_issue: '', 
          current_mileage: '', 
          quotes: [],
          damage_photos: [],
          mr_report_id: '',
          mr_raw_file: null,
          other_files: []
        });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to create repair request' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to create repair request' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const openSubmitModal = async (repairId) => {
    // Get repair details
    const repair = repairs.find(r => r.id === repairId);
    setSubmitModal({ 
      show: true, 
      repairId, 
      repair,
      estimatedCost: repair?.suggested_estimated_cost || repair?.estimated_cost || '', 
      extracting: false,
      extractedQuotes: repair?.extracted_quotes || [],
      historicalVendors: [],
      loadingHistory: false
    });
    
    // Auto-extract quotes if not already done
    if (repair?.attachments?.quotes?.length > 0 && !repair?.extracted_quotes?.length) {
      extractQuoteInfo(repairId);
    }
    
    // Load historical vendor data
    loadHistoricalVendors(repairId);
  };

  const extractQuoteInfo = async (repairId) => {
    setSubmitModal(prev => ({ ...prev, extracting: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/extract-quotes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmitModal(prev => ({ 
          ...prev, 
          extracting: false,
          extractedQuotes: data.extracted_quotes || [],
          estimatedCost: prev.estimatedCost || data.suggested_estimated_cost || ''
        }));
        setMessage({ type: 'success', text: `Extracted info from ${data.quotes_extracted} quote(s)` });
        fetchRepairs(); // Refresh to get updated repair with extracted data
      } else {
        setSubmitModal(prev => ({ ...prev, extracting: false }));
        setMessage({ type: 'error', text: 'Failed to extract quote information' });
      }
    } catch (error) {
      console.error('Extract error:', error);
      setSubmitModal(prev => ({ ...prev, extracting: false }));
      setMessage({ type: 'error', text: 'Failed to extract quote information' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const loadHistoricalVendors = async (repairId) => {
    setSubmitModal(prev => ({ ...prev, loadingHistory: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/historical-vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmitModal(prev => ({ 
          ...prev, 
          loadingHistory: false,
          historicalVendors: data.vendor_comparison || []
        }));
      } else {
        setSubmitModal(prev => ({ ...prev, loadingHistory: false }));
      }
    } catch (error) {
      console.error('Historical vendors error:', error);
      setSubmitModal(prev => ({ ...prev, loadingHistory: false }));
    }
  };

  const openEditModal = (repair) => {
    setEditModal({ show: true, repair, newQuotes: [], saving: false });
  };

  const handleAddQuotes = async () => {
    if (!editModal.newQuotes.length) {
      setMessage({ type: 'error', text: 'Please select quote files to add' });
      return;
    }
    
    setEditModal(prev => ({ ...prev, saving: true }));
    try {
      const formDataToSend = new FormData();
      for (const file of editModal.newQuotes) {
        formDataToSend.append('quotes', file);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${editModal.repair.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Quotes added successfully' });
        fetchRepairs();
        setEditModal({ show: false, repair: null, newQuotes: [], newMRFile: null, newMRReportId: '', saving: false });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to add quotes' });
        setEditModal(prev => ({ ...prev, saving: false }));
      }
    } catch (error) {
      console.error('Add quotes error:', error);
      setMessage({ type: 'error', text: 'Failed to add quotes' });
      setEditModal(prev => ({ ...prev, saving: false }));
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleUploadMR = async () => {
    if (!editModal.newMRFile && !editModal.newMRReportId) {
      setMessage({ type: 'error', text: 'Please select an M&R file or saved report' });
      return;
    }
    
    setEditModal(prev => ({ ...prev, saving: true }));
    try {
      const formDataToSend = new FormData();
      if (editModal.newMRFile) {
        formDataToSend.append('mr_raw_file', editModal.newMRFile);
      }
      if (editModal.newMRReportId) {
        formDataToSend.append('mr_report_id', editModal.newMRReportId);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${editModal.repair.id}/upload-mr`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });
      
      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: result.replaced_existing ? 'M&R report replaced successfully' : 'M&R report uploaded successfully' });
        fetchRepairs();
        setEditModal({ show: false, repair: null, newQuotes: [], newMRFile: null, newMRReportId: '', saving: false });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to upload M&R' });
        setEditModal(prev => ({ ...prev, saving: false }));
      }
    } catch (error) {
      console.error('Upload M&R error:', error);
      setMessage({ type: 'error', text: 'Failed to upload M&R' });
      setEditModal(prev => ({ ...prev, saving: false }));
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const resubmitRepair = async (repairId, estimatedCost) => {
    try {
      const formData = new FormData();
      if (estimatedCost) {
        formData.append('estimated_cost', estimatedCost);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/resubmit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Repair resubmitted for approval' });
        fetchRepairs();
        setEditModal({ show: false, repair: null, newQuotes: [], newMRFile: null, newMRReportId: '', saving: false });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to resubmit' });
      }
    } catch (error) {
      console.error('Resubmit error:', error);
      setMessage({ type: 'error', text: 'Failed to resubmit repair' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const deleteRepair = async () => {
    if (!deleteModal.repair) return;
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs/${deleteModal.repair.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Repair deleted successfully' });
        fetchRepairs();
        setDeleteModal({ show: false, repair: null, deleting: false });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete repair' });
        setDeleteModal(prev => ({ ...prev, deleting: false }));
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: 'Failed to delete repair' });
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Export repair summary PDF for WhatsApp sharing
  const exportForWhatsApp = async (repairId) => {
    try {
      setMessage({ type: 'info', text: 'Generating PDF for WhatsApp...' });
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/export-whatsapp`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate PDF');
      }
      
      const data = await response.json();
      
      // Download PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.pdf}`;
      link.download = data.filename;
      link.click();
      
      setMessage({ type: 'success', text: '📱 PDF downloaded! Share via WhatsApp for approval.' });
    } catch (error) {
      console.error('Export WhatsApp error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to generate PDF' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const submitForApproval = async () => {
    try {
      const formData = new FormData();
      formData.append('estimated_cost', parseFloat(submitModal.estimatedCost) || 0);

      const response = await fetch(`${BACKEND_URL}/api/repairs/${submitModal.repairId}/submit-approval`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'Submitted! Running AI analysis...' });
        setSubmitModal({ show: false, repairId: null, repair: null, estimatedCost: '', extracting: false, extractedQuotes: [], historicalVendors: [], loadingHistory: false });
        
        // Auto-trigger AI analysis after submission
        if (data.analysis_pending) {
          try {
            await fetch(`${BACKEND_URL}/api/repairs/${submitModal.repairId}/analyze`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setMessage({ type: 'success', text: 'Submitted with AI analysis complete!' });
          } catch (e) {
            console.error('AI analysis error:', e);
          }
        }
        
        fetchRepairs();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to submit' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to submit for approval' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const analyzeRepair = async (repairId) => {
    try {
      setAnalysisModal({ show: true, repair: null, loading: true });
      setMessage({ type: 'info', text: 'Analyzing repair with AI... This may take a moment.' });
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'AI analysis complete!' });
        setAnalysisModal({ show: true, repair: { ...repairs.find(r => r.id === repairId), analysis: data }, loading: false });
        fetchRepairs();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Analysis failed' });
        setAnalysisModal({ show: false, repair: null, loading: false });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to analyze repair' });
      setAnalysisModal({ show: false, repair: null, loading: false });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  const viewAnalysis = (repair) => {
    setAnalysisModal({ show: true, repair, loading: false });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-active',
      pending_approval: 'badge-pending',
      pending_additional_info: 'badge-pending',
      approved: 'badge-approved',
      rejected: 'badge-rejected'
    };
    return badges[status] || 'badge-active';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'DRAFT',
      pending_approval: 'PENDING APPROVAL',
      pending_additional_info: 'INFO REQUIRED',
      approved: 'APPROVED',
      rejected: 'REJECTED'
    };
    return labels[status] || status?.replace('_', ' ').toUpperCase();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount);
  };

  const filteredRepairs = filter === 'mine' 
    ? repairs.filter(r => r.created_by === user?.email)
    : repairs;

  // Mobile card renderer for repairs
  const renderRepairCard = (repair, idx) => (
    <div
      key={idx}
      data-testid={`repair-card-${idx}`}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '0.75rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #E2E8F0',
      }}
    >
      {/* Header: Vehicle + Status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <span className="vin-plate" style={{ fontSize: '0.75rem' }}>{repair.vehicle_no}</span>
        <span className={`badge ${getStatusBadge(repair.status)}`} style={{ fontSize: '0.65rem' }}>
          {getStatusLabel(repair.status)}
        </span>
      </div>
      
      {/* Description */}
      <p style={{ fontSize: '0.9rem', fontWeight: '500', margin: '0 0 0.25rem 0', color: '#1E293B' }}>
        {repair.description}
      </p>
      
      {/* Issue */}
      <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0 0 0.5rem 0' }}>
        {repair.reported_issue?.substring(0, 80)}{repair.reported_issue?.length > 80 ? '...' : ''}
      </p>
      
      {/* Meta: Cost + Mileage */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: 'JetBrains Mono', fontWeight: '600', fontSize: '0.9rem', color: '#003366' }}>
          {formatCurrency(repair.estimated_cost)}
        </span>
        <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
          {repair.current_mileage ? `${repair.current_mileage.toLocaleString()} km` : ''}
        </span>
      </div>
      
      {/* Additional Info Request */}
      {repair.additional_info_requested && (
        <div style={{ fontSize: '0.75rem', color: '#DC2626', marginBottom: '0.5rem', padding: '0.5rem', background: '#FEF2F2', borderRadius: '4px' }}>
          ⚠️ {repair.additional_info_requested.reason}
        </div>
      )}
      
      {/* Action Buttons */}
      {(repair.status === 'draft' || repair.status === 'pending_additional_info') && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
          <button 
            className="btn btn-primary"
            style={{ flex: 1, padding: '0.5rem', fontSize: '0.75rem' }}
            onClick={() => openSubmitModal(repair)}
          >
            📤 Submit
          </button>
          <button 
            className="btn btn-secondary"
            style={{ flex: 1, padding: '0.5rem', fontSize: '0.75rem' }}
            onClick={() => openEditModal(repair)}
          >
            ✏️ Edit
          </button>
          <button 
            className="btn btn-danger"
            style={{ padding: '0.5rem', fontSize: '0.75rem' }}
            onClick={() => setDeleteModal({ show: true, repair, deleting: false })}
          >
            🗑️
          </button>
        </div>
      )}
      
      {/* View-only buttons for submitted repairs */}
      {repair.status !== 'draft' && repair.status !== 'pending_additional_info' && (
        <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
          <button 
            className="btn btn-secondary"
            style={{ flex: 1, padding: '0.5rem', fontSize: '0.75rem' }}
            onClick={() => exportForWhatsApp(repair.id)}
          >
            📱 WhatsApp
          </button>
          {repair.analysis && (
            <button 
              className="btn btn-secondary"
              style={{ flex: 1, padding: '0.5rem', fontSize: '0.75rem' }}
              onClick={() => viewAnalysis(repair)}
            >
              🤖 AI
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="repairs-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.75rem' : 0 }}>
        <div>
          <h2 style={isMobile ? { fontSize: '1.25rem' } : {}}>Repair Requests</h2>
          <p style={isMobile ? { fontSize: '0.8rem' } : {}}>Create and manage vehicle repair requests</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(!showForm)} 
          data-testid="create-repair-btn"
          style={isMobile ? { fontSize: '0.85rem', padding: '0.5rem 1rem' } : {}}
        >
          + {isMobile ? 'New Repair' : 'New Repair Request'}
        </button>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      {/* Submit for Approval Modal - Enhanced with Quote Extraction */}
      {submitModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '2rem'
        }}>
          <div className="card" style={{ width: '700px', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Submit for Approval</h3>
              <button 
                className="btn btn-secondary" 
                onClick={() => setSubmitModal({ show: false, repairId: null, repair: null, estimatedCost: '', extracting: false, extractedQuotes: [], historicalVendors: [], loadingHistory: false })}
                style={{ padding: '0.25rem 0.75rem' }}
              >
                ✕
              </button>
            </div>
            
            {/* Repair Info */}
            <div style={{ background: '#F8FAFC', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="vin-plate">{submitModal.repair?.vehicle_no}</span>
                <span style={{ color: '#64748B' }}>{submitModal.repair?.description}</span>
              </div>
            </div>

            {/* Extracted Quotes Section */}
            {submitModal.repair?.attachments?.quotes?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0 }}>💰 Uploaded Quotes ({submitModal.repair.attachments.quotes.length})</h4>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => extractQuoteInfo(submitModal.repairId)}
                    disabled={submitModal.extracting}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                  >
                    {submitModal.extracting ? '⏳ Extracting...' : '🔄 Pull Info'}
                  </button>
                </div>
                
                {submitModal.extracting && (
                  <div style={{ textAlign: 'center', padding: '1rem', background: '#FEF3C7', borderRadius: '4px' }}>
                    <p style={{ margin: 0, color: '#92400E' }}>🤖 AI is extracting info from quotes...</p>
                  </div>
                )}
                
                {submitModal.extractedQuotes.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ fontSize: '0.875rem' }}>
                      <thead>
                        <tr>
                          <th>Vendor</th>
                          <th>Items</th>
                          <th>Subtotal</th>
                          <th>GST</th>
                          <th style={{ fontWeight: '700' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submitModal.extractedQuotes.map((quote, idx) => {
                          const data = quote.data || {};
                          return (
                            <tr key={idx}>
                              <td>
                                <strong>{data.supplier || 'Unknown'}</strong>
                                {data.quote_date && <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{data.quote_date}</div>}
                              </td>
                              <td>
                                {data.items?.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.75rem' }}>
                                    {data.items.slice(0, 3).map((item, i) => (
                                      <li key={i}>{item.name}: ${item.cost?.toFixed(2) || '?'}</li>
                                    ))}
                                    {data.items.length > 3 && <li>+{data.items.length - 3} more</li>}
                                  </ul>
                                ) : '-'}
                              </td>
                              <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                {data.subtotal ? `$${data.subtotal.toFixed(2)}` : '-'}
                              </td>
                              <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                {data.gst ? `$${data.gst.toFixed(2)}` : '-'}
                              </td>
                              <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: '700', color: '#059669' }}>
                                {data.total ? `$${data.total.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Historical Vendors Section */}
            {submitModal.historicalVendors.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>📊 Historical Vendors (Similar Repairs)</h4>
                <div style={{ background: '#EFF6FF', padding: '0.75rem', borderRadius: '4px', fontSize: '0.875rem' }}>
                  <p style={{ margin: 0, marginBottom: '0.5rem', color: '#1E40AF' }}>
                    💡 AI found {submitModal.historicalVendors.length} vendor(s) who did similar work before:
                  </p>
                  <table className="table" style={{ fontSize: '0.75rem', margin: 0 }}>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Jobs Done</th>
                        <th>Avg Cost</th>
                        <th>Total Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submitModal.historicalVendors.slice(0, 5).map((v, idx) => (
                        <tr key={idx}>
                          <td><strong>{v.vendor}</strong></td>
                          <td>{v.total_jobs}</td>
                          <td style={{ fontFamily: 'JetBrains Mono, monospace', color: '#059669' }}>
                            ${v.avg_cost?.toFixed(2)}
                          </td>
                          <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            ${v.total_cost?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {submitModal.loadingHistory && (
              <p style={{ color: '#64748B', fontSize: '0.875rem' }}>Loading historical vendor data...</p>
            )}

            {/* Estimated Cost Input */}
            <div className="form-group">
              <label className="form-label">Estimated Cost ($)</label>
              <input
                type="number"
                className="form-input"
                value={submitModal.estimatedCost}
                onChange={(e) => setSubmitModal({ ...submitModal, estimatedCost: e.target.value })}
                placeholder="0.00"
                data-testid="estimated-cost-input"
                style={{ fontSize: '1.25rem', fontWeight: '600' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.5rem' }}>
                {submitModal.extractedQuotes.length > 0 
                  ? '💡 Pre-filled from extracted quotes. You can adjust if needed.'
                  : 'Cost determines which approval levels are required'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button 
                className="btn btn-primary" 
                onClick={submitForApproval} 
                data-testid="confirm-submit-btn"
                disabled={!submitModal.estimatedCost}
              >
                Submit for Approval
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setSubmitModal({ show: false, repairId: null, repair: null, estimatedCost: '', extracting: false, extractedQuotes: [], historicalVendors: [], loadingHistory: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card" data-testid="repair-form">
          <h3 style={{ marginBottom: '1.5rem' }}>New Repair Request</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Vehicle (Type to search)</label>
              <VehicleSearch
                token={token}
                value={formData.vehicle_id}
                onChange={(value) => setFormData({...formData, vehicle_id: value})}
                onVehicleSelect={(vehicle) => {
                  // Auto-populate current mileage from vehicle data
                  if (vehicle.current_mileage) {
                    setFormData(prev => ({
                      ...prev,
                      vehicle_id: vehicle.vehicle_no,
                      current_mileage: vehicle.current_mileage.toString()
                    }));
                  }
                }}
                placeholder="Type vehicle number to search..."
                required={true}
                dataTestId="repair-vehicle-search"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input 
                  className="form-input" 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  required 
                  data-testid="description-input"
                  placeholder="Brief description of repair needed"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Current Mileage (km)</label>
                <input 
                  type="number"
                  className="form-input" 
                  value={formData.current_mileage} 
                  onChange={(e) => setFormData({...formData, current_mileage: e.target.value})} 
                  data-testid="mileage-input"
                  placeholder="e.g., 125000"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Reported Issue (Detailed)</label>
              <textarea 
                className="form-textarea" 
                value={formData.reported_issue} 
                onChange={(e) => setFormData({...formData, reported_issue: e.target.value})} 
                required 
                data-testid="issue-textarea"
                placeholder="Describe the issue in detail - what was observed, when it started, etc."
                style={{ minHeight: '100px' }}
              />
            </div>

            {/* Categorised Attachments Section */}
            <div style={{ background: '#F8FAFC', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#1E293B' }}>📎 Attachments</h4>
              
              {/* M&R Report - Optional */}
              <div className="form-group" style={{ background: '#FEF3C7', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
                <label className="form-label" style={{ color: '#92400E' }} data-testid="mr-report-optional-label">
                  📊 M&R Report <span style={{ color: '#64748B', fontWeight: 'normal' }}>(Optional)</span>
                </label>
                
                {/* Option 1: Select saved report */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.875rem', color: '#64748B' }}>Select from saved reports:</label>
                  <select
                    className="form-select"
                    value={formData.mr_report_id}
                    onChange={(e) => setFormData({...formData, mr_report_id: e.target.value, mr_raw_file: null})}
                    data-testid="mr-report-select"
                  >
                    <option value="">-- Select a saved M&R Report --</option>
                    {filteredMRReports.map((report, idx) => (
                      <option key={idx} value={report.id}>
                        {report.report_name} (Generated: {new Date(report.generated_at).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Option 2: Upload raw M&R file */}
                <div>
                  <label style={{ fontSize: '0.875rem', color: '#64748B' }}>Or upload raw M&R Excel file:</label>
                  <input 
                    type="file" 
                    accept=".xls,.xlsx"
                    onChange={(e) => setFormData({
                      ...formData, 
                      mr_raw_file: e.target.files[0] || null,
                      mr_report_id: ''
                    })} 
                    data-testid="mr-raw-file-input"
                  />
                  {formData.mr_raw_file && (
                    <p style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem' }}>
                      ✓ {formData.mr_raw_file.name} (will be processed automatically)
                    </p>
                  )}
                </div>
                
                <p style={{ fontSize: '0.75rem', color: '#92400E', marginTop: '0.5rem' }}>
                  Optional. Attaching an M&R Report gives the AI analysis more vehicle history to work with.
                </p>
              </div>

              {/* Quotes - Optional */}
              <div className="form-group">
                <label className="form-label">💰 Quotes (Optional)</label>
                <input 
                  type="file" 
                  accept="image/*,application/pdf,.doc,.docx"
                  multiple
                  key={`quotes-${formData.quotes.length}`}
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setFormData({...formData, quotes: [...formData.quotes, ...newFiles]});
                  }} 
                  data-testid="quotes-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                  Upload quote documents or WhatsApp screenshots. Select multiple or add more.
                </p>
                {formData.quotes.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                      {formData.quotes.length} file(s) selected:
                    </p>
                    <ul style={{ marginLeft: '1rem', fontSize: '0.8rem', color: '#059669' }}>
                      {formData.quotes.map((file, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          ✓ {file.name}
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, quotes: formData.quotes.filter((_, i) => i !== idx)})}
                            style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.7rem' }}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Damage Photos - Optional */}
              <div className="form-group">
                <label className="form-label">📷 Damage Photos (Optional)</label>
                <input 
                  type="file" 
                  accept="image/*"
                  multiple
                  key={`photos-${formData.damage_photos.length}`}
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setFormData({...formData, damage_photos: [...formData.damage_photos, ...newFiles]});
                  }} 
                  data-testid="damage-photos-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                  Upload photos showing the damage. Select multiple or add more.
                </p>
                {formData.damage_photos.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                      {formData.damage_photos.length} photo(s) selected:
                    </p>
                    <ul style={{ marginLeft: '1rem', fontSize: '0.8rem', color: '#059669' }}>
                      {formData.damage_photos.map((file, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          ✓ {file.name}
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, damage_photos: formData.damage_photos.filter((_, i) => i !== idx)})}
                            style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.7rem' }}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Other Files - Optional */}
              <div className="form-group">
                <label className="form-label">📁 Other Documents (Optional)</label>
                <input 
                  type="file" 
                  accept="*/*"
                  multiple
                  key={`other-${formData.other_files.length}`}
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setFormData({...formData, other_files: [...formData.other_files, ...newFiles]});
                  }} 
                  data-testid="other-files-input"
                />
                <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                  Any other supporting documents.
                </p>
                {formData.other_files.length > 0 && (
                  <ul style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#059669' }}>
                    {formData.other_files.map((file, idx) => (
                      <li key={idx}>✓ {file.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn btn-primary" data-testid="submit-repair-btn">
                Create Repair Request
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button 
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
          style={isMobile ? { fontSize: '0.8rem', padding: '0.4rem 0.8rem' } : {}}
        >
          All ({repairs.length})
        </button>
        <button 
          className={`btn ${filter === 'mine' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('mine')}
          style={isMobile ? { fontSize: '0.8rem', padding: '0.4rem 0.8rem' } : {}}
        >
          Mine ({repairs.filter(r => r.created_by === user?.email).length})
        </button>
      </div>

      {/* Repairs List - Cards on mobile, Table on desktop */}
      {isMobile ? (
        // Mobile: Card layout
        <div data-testid="repairs-cards">
          {filteredRepairs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
              No repair requests found
            </div>
          ) : (
            filteredRepairs.map((repair, idx) => renderRepairCard(repair, idx))
          )}
        </div>
      ) : (
        // Desktop: Table layout
        <div className="card">
          <table className="table" data-testid="repairs-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Description</th>
                <th>Issue</th>
                <th>Est. Cost</th>
                <th>Mileage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepairs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
                    No repair requests found
                  </td>
                </tr>
              ) : (
                filteredRepairs.map((repair, idx) => (
                  <tr key={idx} data-testid={`repair-row-${idx}`}>
                    <td><span className="vin-plate">{repair.vehicle_no}</span></td>
                    <td>{repair.description}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {repair.reported_issue}
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCurrency(repair.estimated_cost)}
                    </td>
                    <td>{repair.current_mileage ? `${repair.current_mileage.toLocaleString()} km` : '-'}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(repair.status)}`}>
                        {getStatusLabel(repair.status)}
                      </span>
                      {repair.additional_info_requested && (
                        <div style={{ fontSize: '0.7rem', color: '#DC2626', marginTop: '0.25rem' }}>
                          {repair.additional_info_requested.reason}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ position: 'relative' }} className="actions-dropdown-container">
                        {/* Actions Dropdown Button */}
                        {(repair.status === 'draft' || repair.status === 'pending_additional_info') && (
                          <button 
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionDropdown(actionDropdown === repair.id ? null : repair.id);
                            }}
                            style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem' }}
                          >
                            Actions ▾
                          </button>
                        )}
                      
                      {/* Dropdown Menu */}
                      {actionDropdown === repair.id && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          background: 'white',
                          border: '1px solid #E2E8F0',
                          borderRadius: '4px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 100,
                          minWidth: '180px',
                          marginTop: '4px'
                        }}>
                          {repair.status === 'draft' && (
                            <>
                              <button 
                                onClick={() => { openSubmitModal(repair.id); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#F1F5F9'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                                data-testid={`submit-approval-btn-${idx}`}
                              >
                                📤 Submit for Approval
                              </button>
                              <button 
                                onClick={() => { analyzeRepair(repair.id); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#F1F5F9'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                                data-testid={`analyze-btn-${idx}`}
                              >
                                🤖 Analyse
                              </button>
                              <button 
                                onClick={() => { openEditModal(repair); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#F1F5F9'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                ✏️ Edit
                              </button>
                              <button 
                                onClick={() => { exportForWhatsApp(repair.id); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem', color: '#25D366'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#DCFCE7'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                                data-testid={`whatsapp-export-btn-${idx}`}
                              >
                                📱 Export for WhatsApp
                              </button>
                              <div style={{ borderTop: '1px solid #E2E8F0', margin: '4px 0' }}></div>
                              <button 
                                onClick={() => { setDeleteModal({ show: true, repair, deleting: false }); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem', color: '#DC2626'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#FEE2E2'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                🗑️ Delete
                              </button>
                            </>
                          )}
                          {repair.status === 'pending_additional_info' && (
                            <>
                              <button 
                                onClick={() => { openEditModal(repair); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#F1F5F9'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                ➕ Add Info
                              </button>
                              <button 
                                onClick={() => { analyzeRepair(repair.id); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#F1F5F9'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                🤖 Analyse
                              </button>
                              <button 
                                onClick={() => { exportForWhatsApp(repair.id); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem', color: '#25D366'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#DCFCE7'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                📱 Export for WhatsApp
                              </button>
                              <div style={{ borderTop: '1px solid #E2E8F0', margin: '4px 0' }}></div>
                              <button 
                                onClick={() => { setDeleteModal({ show: true, repair, deleting: false }); setActionDropdown(null); }}
                                style={{ 
                                  display: 'block', width: '100%', textAlign: 'left',
                                  padding: '0.75rem 1rem', border: 'none', background: 'none',
                                  cursor: 'pointer', fontSize: '0.875rem', color: '#DC2626'
                                }}
                                onMouseOver={(e) => e.target.style.background = '#FEE2E2'}
                                onMouseOut={(e) => e.target.style.background = 'none'}
                              >
                                🗑️ Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* View Analysis Button (always visible if analysis exists) */}
                      {repair.analysis && (
                        <button 
                          className="btn btn-secondary"
                          onClick={() => viewAnalysis(repair)}
                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', marginLeft: '0.5rem' }}
                          data-testid={`view-analysis-btn-${idx}`}
                        >
                          📊 View Analysis
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '400px', margin: 0 }}>
            <h3 style={{ marginBottom: '1rem', color: '#DC2626' }}>🗑️ Delete Repair Request</h3>
            <p style={{ color: '#64748B', marginBottom: '1rem' }}>
              Are you sure you want to delete this repair request?
            </p>
            <div style={{ background: '#F8FAFC', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <strong>{deleteModal.repair?.vehicle_no}</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>{deleteModal.repair?.description}</p>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#DC2626', marginBottom: '1rem' }}>
              ⚠️ This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                className="btn" 
                onClick={deleteRepair}
                disabled={deleteModal.deleting}
                style={{ background: '#DC2626', color: 'white' }}
              >
                {deleteModal.deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setDeleteModal({ show: false, repair: null, deleting: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Modal */}
      {analysisModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '2rem'
        }}>
          <div className="card" style={{ width: '900px', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3>🤖 AI Analysis Results</h3>
              <button 
                className="btn btn-secondary" 
                onClick={() => setAnalysisModal({ show: false, repair: null, loading: false })}
                style={{ padding: '0.5rem 1rem' }}
              >
                ✕ Close
              </button>
            </div>

            {analysisModal.loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner"></div>
                <p style={{ marginTop: '1rem', color: '#64748B' }}>Analyzing repair with Claude Sonnet 4...</p>
              </div>
            ) : analysisModal.repair?.analysis ? (
              <div>
                {/* Header Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <span className="vin-plate">{analysisModal.repair.vehicle_no}</span>
                    <p style={{ marginTop: '0.5rem', fontWeight: '600' }}>{analysisModal.repair.description}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {analysisModal.repair.analysis.approval_recommendation && (
                      <span className={`badge ${
                        analysisModal.repair.analysis.approval_recommendation === 'APPROVE' ? 'badge-approved' :
                        analysisModal.repair.analysis.approval_recommendation === 'REJECT' ? 'badge-rejected' : 'badge-pending'
                      }`} style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                        {analysisModal.repair.analysis.approval_recommendation}
                      </span>
                    )}
                    {analysisModal.repair.analysis.current_mileage && (
                      <p style={{ marginTop: '0.5rem', color: '#64748B' }}>
                        Mileage: {analysisModal.repair.analysis.current_mileage?.toLocaleString()} km
                      </p>
                    )}
                  </div>
                </div>

                {/* Approval Reason */}
                {analysisModal.repair.analysis.approval_reason && (
                  <div style={{ padding: '1rem', background: '#EFF6FF', borderRadius: '4px', marginBottom: '1.5rem', border: '1px solid #BFDBFE' }}>
                    <strong>AI Recommendation:</strong> {analysisModal.repair.analysis.approval_reason}
                  </div>
                )}

                {/* Frequent Repairs Alert */}
                {analysisModal.repair.analysis.frequent_repairs_alert?.length > 0 && (
                  <div style={{ padding: '1rem', background: '#FEF2F2', borderRadius: '4px', marginBottom: '1.5rem', border: '1px solid #FECACA' }}>
                    <strong>⚠️ Frequent Repairs Alert:</strong>
                    <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                      {analysisModal.repair.analysis.frequent_repairs_alert.map((alert, idx) => (
                        <li key={idx} style={{ color: '#991B1B' }}>{alert}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cost Comparison */}
                {analysisModal.repair.analysis.cost_comparison?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>💰 Cost Comparison</h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Current Quote</th>
                          <th>Historical Avg</th>
                          <th>Variance</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisModal.repair.analysis.cost_comparison.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.item}</td>
                            <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(item.current_quote)}</td>
                            <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(item.historical_average)}</td>
                            <td style={{ 
                              color: item.variance_percent > 20 ? '#DC2626' : item.variance_percent < -10 ? '#059669' : '#64748B',
                              fontWeight: '600'
                            }}>
                              {item.variance_percent > 0 ? '+' : ''}{item.variance_percent}%
                            </td>
                            <td style={{ fontSize: '0.875rem' }}>{item.comment}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Cross-Fleet Rate Comparison */}
                {analysisModal.repair.analysis.cross_fleet_rates?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>🔍 Cross-Fleet Vendor Rates (Similar Items)</h4>
                    <p style={{ fontSize: '0.875rem', color: '#64748B', marginBottom: '0.75rem' }}>
                      Rates for similar repairs across ALL vehicles in fleet
                    </p>
                    <table className="table" style={{ fontSize: '0.875rem' }}>
                      <thead>
                        <tr>
                          <th>Vendor</th>
                          <th>Jobs Done</th>
                          <th>Avg Cost</th>
                          <th>Total Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisModal.repair.analysis.cross_fleet_rates.map((rate, idx) => (
                          <tr key={idx}>
                            <td><strong>{rate.vendor}</strong></td>
                            <td>{rate.jobs_done}</td>
                            <td style={{ fontFamily: 'JetBrains Mono', color: '#059669' }}>
                              {formatCurrency(rate.avg_cost)}
                            </td>
                            <td style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatCurrency(rate.total_spent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Repair Frequency */}
                {analysisModal.repair.analysis.repair_frequency?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>🔧 Repair Frequency Analysis</h4>
                    {analysisModal.repair.analysis.repair_frequency.map((item, idx) => (
                      <div key={idx} style={{ padding: '0.75rem', background: '#F8FAFC', borderRadius: '4px', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <strong>{item.component}</strong>
                          <span>Repaired {item.times_repaired}x (avg {item.average_interval_months} months interval)</span>
                        </div>
                        {item.alert && (
                          <p style={{ color: '#DC2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>⚠️ {item.alert}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Mileage Analysis */}
                {analysisModal.repair.analysis.mileage_analysis && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>📏 Mileage Analysis</h4>
                    <div style={{ padding: '1rem', background: '#F0FDF4', borderRadius: '4px', border: '1px solid #BBF7D0' }}>
                      <p><strong>Expected Wear:</strong> {analysisModal.repair.analysis.mileage_analysis.expected_wear}</p>
                      {analysisModal.repair.analysis.mileage_analysis.mileage_since_last_similar_repair && (
                        <p style={{ marginTop: '0.5rem' }}>
                          <strong>Km since last similar repair:</strong> {analysisModal.repair.analysis.mileage_analysis.mileage_since_last_similar_repair?.toLocaleString()} km
                        </p>
                      )}
                      <p style={{ marginTop: '0.5rem', color: '#64748B' }}>{analysisModal.repair.analysis.mileage_analysis.comment}</p>
                    </div>
                  </div>
                )}

                {/* Similar Repairs */}
                {analysisModal.repair.analysis.similar_repairs?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>📋 Similar Past Repairs</h4>
                    {analysisModal.repair.analysis.similar_repairs.slice(0, 5).map((item, idx) => (
                      <div key={idx} style={{ padding: '0.75rem', background: '#F8FAFC', borderRadius: '4px', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <strong>{item.date}</strong> - {item.description}
                          <p style={{ fontSize: '0.75rem', color: '#64748B' }}>{item.supplier}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontFamily: 'JetBrains Mono', fontWeight: '600' }}>{formatCurrency(item.cost)}</span>
                          <p style={{ fontSize: '0.75rem', color: '#64748B' }}>{item.relevance}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommendations */}
                {analysisModal.repair.analysis.recommendations?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>💡 Recommendations</h4>
                    {analysisModal.repair.analysis.recommendations.map((rec, idx) => (
                      <div key={idx} style={{ 
                        padding: '1rem', 
                        background: rec.priority === 'HIGH' ? '#FEF2F2' : rec.priority === 'MEDIUM' ? '#FFFBEB' : '#F0FDF4',
                        borderRadius: '4px', 
                        marginBottom: '0.5rem',
                        borderLeft: `4px solid ${rec.priority === 'HIGH' ? '#DC2626' : rec.priority === 'MEDIUM' ? '#F59E0B' : '#10B981'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <strong>{rec.action}</strong>
                            <p style={{ fontSize: '0.875rem', color: '#64748B', marginTop: '0.25rem' }}>{rec.reason}</p>
                          </div>
                          <span className={`badge ${rec.priority === 'HIGH' ? 'badge-rejected' : rec.priority === 'MEDIUM' ? 'badge-pending' : 'badge-approved'}`}>
                            {rec.priority}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Insights */}
                {analysisModal.repair.analysis.insights?.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ marginBottom: '0.75rem' }}>🔍 Key Insights</h4>
                    <ul style={{ marginLeft: '1rem' }}>
                      {analysisModal.repair.analysis.insights.map((insight, idx) => (
                        <li key={idx} style={{ marginBottom: '0.5rem' }}>{insight}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Analysed timestamp */}
                <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '1rem' }}>
                  Analysed at {new Date(analysisModal.repair.analysis.analyzed_at).toLocaleString()} by {analysisModal.repair.analysis.analyzed_by}
                </p>
              </div>
            ) : (
              <p style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>No analysis data available</p>
            )}
          </div>
        </div>
      )}

      {/* Edit/Add Quotes Modal */}
      {editModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '2rem'
        }}>
          <div className="card" style={{ width: '600px', maxHeight: '90vh', overflow: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>
                {editModal.repair?.status === 'pending_additional_info' ? '➕ Add Additional Information' : '✏️ Edit Repair Request'}
              </h3>
              <button 
                className="btn btn-secondary" 
                onClick={() => setEditModal({ show: false, repair: null, newQuotes: [], saving: false })}
                style={{ padding: '0.25rem 0.75rem' }}
              >
                ✕
              </button>
            </div>
            
            {/* Repair Info */}
            <div style={{ background: '#F8FAFC', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="vin-plate">{editModal.repair?.vehicle_no}</span>
                <span style={{ color: '#64748B' }}>{editModal.repair?.description}</span>
              </div>
              {editModal.repair?.additional_info_requested && (
                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#FEF2F2', borderRadius: '4px', border: '1px solid #FECACA' }}>
                  <strong style={{ color: '#DC2626' }}>📋 Request from approver:</strong>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#991B1B' }}>{editModal.repair.additional_info_requested.reason}</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#64748B' }}>
                    Requested by {editModal.repair.additional_info_requested.requested_by} on {new Date(editModal.repair.additional_info_requested.requested_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Existing Quotes */}
            {editModal.repair?.attachments?.quotes?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem' }}>📄 Existing Quotes ({editModal.repair.attachments.quotes.length})</h4>
                <ul style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#059669' }}>
                  {editModal.repair.attachments.quotes.map((q, idx) => (
                    <li key={idx}>✓ {q.filename}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Add New Quotes */}
            <div className="form-group">
              <label className="form-label">💰 Add New Quote(s)</label>
              <input 
                type="file" 
                accept="image/*,application/pdf,.doc,.docx"
                multiple
                onChange={(e) => setEditModal({...editModal, newQuotes: Array.from(e.target.files)})} 
              />
              <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                Upload additional quote documents or WhatsApp screenshots.
              </p>
              {editModal.newQuotes.length > 0 && (
                <ul style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#059669' }}>
                  {editModal.newQuotes.map((file, idx) => (
                    <li key={idx}>✓ {file.name} (new)</li>
                  ))}
                </ul>
              )}
            </div>

            {/* M&R Report Section */}
            <div className="form-group" style={{ marginTop: '1rem', padding: '1rem', background: '#FEF3C7', borderRadius: '8px' }}>
              <label className="form-label">📊 M&R Report</label>
              
              {/* Show existing M&R if any */}
              {editModal.repair?.attachments?.mr_report ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#059669', margin: 0 }}>
                    ✓ Current: {editModal.repair.attachments.mr_report.filename}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#64748B', margin: '0.25rem 0 0 0' }}>
                    Upload a new file below to replace with latest version
                  </p>
                </div>
              ) : (
                <p style={{ fontSize: '0.875rem', color: '#DC2626', marginBottom: '0.75rem' }}>
                  ⚠️ No M&R report attached. Upload one below.
                </p>
              )}
              
              {/* Option 1: Select from saved reports */}
              {savedMRReports.length > 0 && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <select
                    className="form-control"
                    value={editModal.newMRReportId}
                    onChange={(e) => setEditModal({...editModal, newMRReportId: e.target.value, newMRFile: null})}
                    style={{ marginBottom: '0.25rem' }}
                  >
                    <option value="">-- Select Saved M&R Report --</option>
                    {savedMRReports
                      .filter(r => r.vehicle_no === editModal.repair?.vehicle_no)
                      .map(report => (
                        <option key={report.id} value={report.id}>
                          {report.report_name || report.vehicle_no} - {new Date(report.generated_at).toLocaleDateString()}
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}
              
              {/* Option 2: Upload file */}
              <p style={{ fontSize: '0.75rem', color: '#64748B', margin: '0.5rem 0' }}>Or upload M&R file:</p>
              <input 
                type="file" 
                accept=".pdf,.xls,.xlsx"
                onChange={(e) => setEditModal({...editModal, newMRFile: e.target.files[0], newMRReportId: ''})} 
              />
              {editModal.newMRFile && (
                <p style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.25rem' }}>
                  ✓ {editModal.newMRFile.name} (ready to upload)
                </p>
              )}
              
              {/* Upload M&R Button */}
              {(editModal.newMRFile || editModal.newMRReportId) && (
                <button 
                  className="btn btn-primary" 
                  onClick={handleUploadMR}
                  disabled={editModal.saving}
                  style={{ marginTop: '0.75rem' }}
                >
                  {editModal.saving ? '⏳ Uploading...' : '📤 Upload M&R Report'}
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              {editModal.newQuotes.length > 0 && (
                <button 
                  className="btn btn-secondary" 
                  onClick={handleAddQuotes}
                  disabled={editModal.saving}
                >
                  {editModal.saving ? '⏳ Saving...' : '💾 Save Quotes'}
                </button>
              )}
              {editModal.repair?.status === 'pending_additional_info' && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => resubmitRepair(editModal.repair.id, editModal.repair.estimated_cost)}
                  disabled={editModal.saving}
                >
                  🔄 Resubmit for Approval
                </button>
              )}
              <button 
                className="btn btn-secondary" 
                onClick={() => setEditModal({ show: false, repair: null, newQuotes: [], newMRFile: null, newMRReportId: '', saving: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Repairs;
